import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {Game} from "../Game";
import {FontStyle, FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/text/TextStyleOptions";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {Sound} from "../sound/Sound";
import {GameModeNames} from "../server/GameModeNames";

export const FSWinTextStyle: TextStyleOptions = {
    fontFamily: OpenSans.FAMILY,
    fontStyle: FontStyle.NORMAL,
    fontWeight: FontWeight.NORMAL,
    align: "center",
    dropShadow: true,
    dropShadowAngle: 1.5,
    dropShadowColor: "#000",
    dropShadowDistance: 6,
    fill: "#ffffff",
    fontSize: 40,
    lineJoin: "round",
    stroke: "#000",
    strokeThickness: 8
};

export class FeatureIntro extends PIXI.Container {
    private _fsIntroAnimation: PIXI.spine.Spine;
    private _sfsIntroAnimation: PIXI.spine.Spine;
    private _mayhemAnimation: PIXI.spine.Spine;
    private _mayhemReTriggerAnimation: PIXI.spine.Spine;
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _bgOverlay: PIXI.Graphics;
    private _isExited: boolean;

    constructor() {
        super();
        this.name = "FeatureIntro";
        this.init();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showFSIntro(callback: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2
            }),
            duration: number = 0.5;

        tl.add(() => {
            this._button.alpha = 0;
            this._button.visible = true;
            this._bgOverlay.alpha = 0;
            this._bgOverlay.visible = true;
            this._fsIntroAnimation.alpha = 0;
            this._fsIntroAnimation.visible = true;
            this._fsIntroAnimation.state.setAnimation(0, 'in', false);

            this._clickCallback = callback;

            Game.soundPlayer.stopSound(Sound.MAIN_GAME_MUSIC);
            Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
            Game.soundPlayer.stopSound(Sound.AS_SNARE);
            Game.soundPlayer.stopSound(Sound.SNARE_PLAYER);
            Game.soundPlayer.playSound(Sound.FS_ENTER, 1);
            Game.soundPlayer.playSound(Sound.FS_INTRO, 1, Number.MAX_VALUE);
        });

        tl.to(this._fsIntroAnimation, {
            duration: duration,
            alpha: 1
        }, "<");

        tl.add(() => {
            this._fsIntroAnimation.state.setAnimation(0, 'loop', true);
        });

        // #433
        tl.add(this.handleAutoPlay(duration, true));

        return tl;
    }

    public showSFSIntro(callback: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2
            }),
            duration: number = 0.5;

        tl.add(() => {
            this._button.alpha = 0;
            this._button.visible = true;
            this._bgOverlay.alpha = 0;
            this._bgOverlay.visible = true;

            this._sfsIntroAnimation.alpha = 0;
            this._sfsIntroAnimation.visible = true;
            this._sfsIntroAnimation.state.setAnimation(0, 'in', false);

            this._clickCallback = callback;

            Game.soundPlayer.stopSound(Sound.MAIN_GAME_MUSIC);
            Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
            Game.soundPlayer.stopSound(Sound.AS_SNARE);
            Game.soundPlayer.stopSound(Sound.SNARE_PLAYER);
            Game.soundPlayer.playSound(Sound.SFS_INTRO, 1, Number.MAX_VALUE);
        });

        tl.to(this._sfsIntroAnimation, {
            duration: duration,
            alpha: 1
        }, "<");

        tl.add(() => {
            this._sfsIntroAnimation.state.setAnimation(0, 'loop', true);
        });

        // #433
        tl.add(this.handleAutoPlay(duration, true));

        return tl;
    }

    public showMayhemIntro(callback: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2
            }),
            duration: number = 0.5;

        tl.add(() => {
            this._button.alpha = 0;
            this._button.visible = true;
            this._mayhemAnimation.alpha = 0;
            this._mayhemAnimation.visible = true;
            this._mayhemAnimation.state.setAnimation(0, 'in', false);

            this._clickCallback = callback;

            Game.soundPlayer.stopSound(Sound.MAIN_GAME_MUSIC);
            Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
            Game.soundPlayer.stopSound(Sound.AS_SNARE);
            Game.soundPlayer.stopSound(Sound.SNARE_PLAYER);
            Game.soundPlayer.stopSound(Sound.FS_LOOP);
            Game.soundPlayer.playSound(Sound.SFS_INTRO, 1, Number.MAX_VALUE);
        });

        tl.to(this._mayhemAnimation, {
            duration: duration,
            alpha: 1
        }, "<");

        tl.add(() => {
            // #489
            if (this._isExited) {
                this._mayhemAnimation.alpha = 0;
                this._mayhemAnimation.state.clearTracks();
            } else {
                this._mayhemAnimation.state.setAnimation(0, 'loop', true);
            }
        });

        // #433
        tl.add(this.handleAutoPlay(duration, false));

        return tl;
    }

    public showMayhemReTriggerIntro(callback: () => void): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2,
            }),
            duration: number = 0.5,
            animName: string = "InOut",
            animDuration: number = this._mayhemReTriggerAnimation.spineData.findAnimation(animName).duration;

        tl.add(() => {
            this._mayhemReTriggerAnimation.alpha = 0;
            this._mayhemReTriggerAnimation.visible = true;
            this._mayhemReTriggerAnimation.state.setAnimation(0, animName, false);
        });

        tl.to(this._mayhemReTriggerAnimation, {
            duration: duration,
            alpha: 1,
        }, "<");

        tl.to(this, {
            duration: animDuration
        });

        tl.add(() => {
            Game.multiplier.updateMultiplier(10);
            callback();
        });

        return tl;
    }

    private init(): void {
        this.createBGOverlay();

        this._button = new ContinueButton();
        this._fsIntroAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("fsIntro"));
        this._sfsIntroAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("sfsIntro"));
        this._mayhemAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("mayhemIntro"));
        this._mayhemReTriggerAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("mayhemRetriggerIntro"));
        this._button.addClickCallback(() => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._fsIntroAnimation.position.set(360, 330);
        this._sfsIntroAnimation.position.set(360, 330);
        this._mayhemAnimation.position.set(360, 400);
        this._mayhemReTriggerAnimation.position.set(360, 400);
        this._button.enable(false);
        this._button.visible = false;
        this._fsIntroAnimation.visible = false;
        this._sfsIntroAnimation.visible = false;
        this._mayhemAnimation.visible = false;
        this._mayhemReTriggerAnimation.visible = false;

        this.addChild(
            this._fsIntroAnimation,
            this._sfsIntroAnimation,
            this._mayhemAnimation,
            this._mayhemReTriggerAnimation,
            this._button
        );

        this.onResize();
    }

    // #433
    private handleAutoPlay(duration: number, showBgOverlay: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this._isExited = false;

        if (showBgOverlay) {
            tl.to(this._bgOverlay, {
                duration: duration,
                alpha: 1
            });
        } else {
            this._bgOverlay.alpha = 0;
            this._bgOverlay.visible = true;
        }

        // #433
        if (Game.model.gameData.wasFeatureBuy && Game.autoPlayPlugin.isAutoplayRound) {
            this._bgOverlay.interactive = true;
            this._bgOverlay.buttonMode = true;

            tl.to(this, {
                duration: 1.5
            }, "<");
            tl.add(() => {
                this._bgOverlay.interactive = false;
                this._bgOverlay.buttonMode = false;

                this.onButtonClick();
            });
        } else {
            tl.to(this._button, {
                duration: duration,
                alpha: 1
            }, "<");
            tl.add(() => {
                this._button.enable(true);
            });
        }

        return tl;
    }

    private onButtonClick(): void {
        if (this._isExited) {
            return;
        }
        this._isExited = true;

        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.5;

        this._button.enable(false);

        Game.backgroundElements.setMode(Game.model.data.nextMode);

        this._mayhemAnimation.state.setAnimation(0, 'out', false);
        this._fsIntroAnimation.state.setAnimation(0, 'out', false);
        this._sfsIntroAnimation.state.setAnimation(0, 'out', false);

        tl.add(() => {
            Game.soundPlayer.stopSound(Sound.FS_INTRO);

            if (Game.model.gameData.scatterCount > 3) {
                Game.isSFSActive = true;
                Game.soundPlayer.playSound(Sound.FS_START, 1);
                Game.soundPlayer.switchMusic(GameModeNames.SUPER_FREESPIN);
            } else if (Game.model.gameData.scatterCount === 3) {
                Game.soundPlayer.playSound(Sound.FS_START, 1);
                Game.soundPlayer.switchMusic(GameModeNames.FREESPIN);
            } else if (Game.model.data.mayhemSpin) {
                Game.soundPlayer.playSound(Sound.FS_START, 1);
                Game.soundPlayer.switchMusic(GameModeNames.MAYHEM);
            }
        });

        tl.to([this._button, this._bgOverlay, this._fsIntroAnimation, this._sfsIntroAnimation, this._mayhemAnimation], {
            duration: duration,
            alpha: 0
        }, 0);
        tl.add(Game.scatterInventory.hideSMeter(), 0);

        tl.add(() => {
            this._button.visible = false;
            this._bgOverlay.visible = false;

            if (Game.model.gameData.scatterCount < 4 && !Game.model.gameData.mayhemSpin) {
                Game.keypadPlugin.setZeroBetSpinCounter(Game.model.gameData.freespinsLeft);
                Game.multiplier.resetMultiplier(Game.model.gameData.multiplier);

                this._clickCallback();
            }
        });

        tl.add(this.doTransitionForSFSOrMayhem());
    }

    private createBGOverlay(): void {
        this._bgOverlay = new PIXI.Graphics().beginFill(0x000000, 0.7).drawRect(-480, -480, 1680, 1680).endFill();
        this._bgOverlay.name = "dark-overlay";
        this._bgOverlay.visible = false;

        // #433
        this._bgOverlay.on('pointerdown', () => {
            this.onButtonClick();
        });

        this.addChild(this._bgOverlay);
    }

    private doTransitionForSFSOrMayhem(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (Game.model.gameData.mayhemSpin || Game.model.gameData.scatterCount > 3) {
            // #410
            tl.add(() => {
                Game.monsterReelsView.toggleMask(true);
            });

            for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
                tl.add(Game.reelsView.reelModels[i].allOut(), (0.1 * i));
            }

            tl.add(() => {
                const extraReels: number = (Game.TOTAL_REELS_NUM - Game.ACTIVE_REELS_NUM),
                    extraRows: number = (Game.TOTAL_REELS_NUM - Game.ACTIVE_ROWS_NUM);

                Game.expandGrid(extraReels, extraRows, true, true);
            });
            tl.to(this, {
                duration: 0.2
            }, "<");

            tl.add(Game.multiplier.updateMultiplier(Game.model.gameData.mayhemSpin ? 10 : 5));

            tl.add(() => {
                Game.keypadPlugin.setZeroBetSpinCounter(Game.model.gameData.freespinsLeft);

                this._clickCallback();
            });
        }

        return tl;
    }
}